// Third-party Imports
import CredentialProvider from 'next-auth/providers/credentials'
import GoogleProvider from 'next-auth/providers/google'
import type { NextAuthOptions } from 'next-auth'

// Runtime Config
import { getRuntimeConfig } from '@/libs/runtime-config'

/** Role IDs: 1 = admin, 2 = normal user */
export const ADMIN_ROLE_ID = 1
export const USER_ROLE_ID = 2
export const SUPERVISOR_ROLE_ID = 3

/** User shape returned from our credentials authorize() and passed into jwt() callback */
interface CredentialsUser {
  id: string
  name?: string | null
  email?: string | null
  username?: string
  role_id?: number
  report_to?: number
  access_token?: string
  tenants?: number[]
  permissions?: Record<string, unknown>
}

export const authOptions: NextAuthOptions = {
  // Note: Using JWT strategy with credentials provider, so no database adapter needed
  secret: process.env.NEXTAUTH_SECRET || 'fallback-secret-for-development-only',
  debug: process.env.NODE_ENV === 'development',

  // ** Configure one or more authentication providers
  // ** Please refer to https://next-auth.js.org/configuration/options#providers for more `providers` options
  providers: [
    CredentialProvider({
      // ** The name to display on the sign in form (e.g. 'Sign in with...')
      // ** For more details on Credentials Provider, visit https://next-auth.js.org/providers/credentials
      name: 'Credentials',
      type: 'credentials',

      /*
       * As we are using our own Sign-in page, we do not need to change
       * username or password attributes manually in following credentials object.
       */
      credentials: {
        email: { label: 'Email', type: 'email' },
        password: { label: 'Password', type: 'password' }
      },
      async authorize(credentials) {
        /*
         * You need to provide your own logic here that takes the credentials submitted and returns either
         * an object representing a user or value that is false/null if the credentials are invalid.
         */
        const { email, password } = credentials as { email: string; password: string }

        if (!email?.trim() || !password) {
          return null
        }

        try {
          // ** Login API Call to backend
          // Use runtime config which supports globalThis.__RUNTIME_CONFIG__ (client) and process.env (server)
          const API_URL = getRuntimeConfig('NEXT_PUBLIC_API_URL', 'http://localhost:5000')

          // Debug logging in development
          if (process.env.NODE_ENV === 'development') {
            console.log('[AUTH DEBUG] API_URL:', API_URL)
            console.log('[AUTH DEBUG] process.env.NEXT_PUBLIC_API_URL:', process.env.NEXT_PUBLIC_API_URL)
          }

          let res: Response
          try {
            res = await fetch(`${API_URL}/auth/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              credentials: 'include', // Important for cookies
              body: JSON.stringify({ email: email.trim(), password })
            })
          } catch (fetchError: any) {
            // Network error (backend not reachable)
            console.error('[AUTH ERROR] Network error:', fetchError)
            console.error('[AUTH ERROR] Attempted URL:', `${API_URL}/auth/login`)
            console.error('[AUTH ERROR] API_URL value:', API_URL)
            throw new Error(JSON.stringify({
              message: `Failed to connect to server at ${API_URL}. Please check if the backend is running.`
            }))
          }

          // Parse JSON response
          const data = await res.json()

          // Log response in development
          if (process.env.NODE_ENV === 'development') {
            console.log('[AUTH DEBUG] Response status:', res.status)
            console.log('[AUTH DEBUG] Full response:', JSON.stringify(data, null, 2))
          }

          // Check response status (backend returns { success: false, message: string })
          if (!res.ok) {
            const errorMessage =
              typeof data.message === 'string'
                ? data.message
                : data.error || 'Invalid email or password'

            throw new Error(JSON.stringify({ message: errorMessage }))
          }

          // Backend returns: { message: "Login successful", data: { access_token: "...", user: {...} } }
          // So user is at: data.data.user
          const userData = data.data?.user
          const accessToken = data.data?.access_token

          if (process.env.NODE_ENV === 'development') {
            console.log('[AUTH DEBUG] Extracted userData:', userData)
            console.log('[AUTH DEBUG] data structure:', {
              hasData: !!data.data,
              dataKeys: data.data ? Object.keys(data.data) : null,
              hasUser: !!data.data?.user,
              hasToken: !!accessToken
            })
          }

          if (!userData) {
            const errorMsg = process.env.NODE_ENV === 'development'
              ? `User data not found. Response: ${JSON.stringify(data, null, 2)}`
              : 'Invalid response from server. User data not found.'
            throw new Error(JSON.stringify({ message: errorMsg }))
          }

          if (!userData.id) {
            const errorMsg = process.env.NODE_ENV === 'development'
              ? `User data missing id field. UserData: ${JSON.stringify(userData, null, 2)}`
              : 'Invalid user data structure.'
            throw new Error(JSON.stringify({ message: errorMsg }))
          }

          // Store the access token with user data so it can be stored in JWT token
          // We'll retrieve it in the jwt callback and then store in localStorage on client side
          // Note: We can't use localStorage here because this runs server-side

          // Return user data for NextAuth session
          return {
            id: userData.id.toString(),
            name: userData.en_name || '',
            email: userData.email || '',
            username: userData.username || '',
            role_id: userData.role_id || 0,
            report_to: userData.report_to || 0,
            access_token: accessToken,  // Store token with user data
            tenants: userData.tenants || [],
            permissions: userData.permissions || {} // Store permissions
          }
        } catch (e: any) {
          // Ensure error is always a JSON string for NextAuth
          let errorMessage = 'Login failed'

          // Check if error message is already JSON
          if (e.message) {
            try {
              const parsed = JSON.parse(e.message)
              if (parsed && parsed.message) {
                errorMessage = parsed.message
              } else {
                errorMessage = e.message
              }
            } catch {
              // Not JSON, use message directly but wrap it
              errorMessage = e.message || 'An unexpected error occurred'
            }
          }

          // Always throw as JSON string for NextAuth
          // NextAuth expects errors to be JSON strings
          throw new Error(JSON.stringify({ message: errorMessage }))
        }
      }
    }),

    GoogleProvider({
      clientId: process.env.GOOGLE_CLIENT_ID as string,
      clientSecret: process.env.GOOGLE_CLIENT_SECRET as string
    })

    // ** ...add more providers here
  ],

  // ** Please refer to https://next-auth.js.org/configuration/options#session for more `session` options
  session: {
    /*
     * Choose how you want to save the user session.
     * The default is `jwt`, an encrypted JWT (JWE) stored in the session cookie.
     * If you use an `adapter` however, NextAuth default it to `database` instead.
     * You can still force a JWT session by explicitly defining `jwt`.
     * When using `database`, the session cookie will only contain a `sessionToken` value,
     * which is used to look up the session in the database.
     * If you use a custom credentials provider, user accounts will not be persisted in a database by NextAuth.js (even if one is configured).
     * The option to use JSON Web Tokens for session tokens must be enabled to use a custom credentials provider.
     */
    strategy: 'jwt',

    // ** Seconds - How long until an idle session expires and is no longer valid
    maxAge: 30 * 24 * 60 * 60 // ** 30 days
  },

  /**
   * Cookie name prefix to avoid collisions when multiple NextAuth apps run on same domain (localhost).
   * This prevents "log in one app -> other app logs out" due to shared `next-auth.*` cookie names.
   */
  cookies: {
    sessionToken: {
      name: 'job-portal-next-auth.session-token',
      options: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: process.env.NODE_ENV === 'production',
      },
    },
    callbackUrl: {
      name: 'job-portal-next-auth.callback-url',
      options: {
        sameSite: 'lax',
        path: '/',
        secure: process.env.NODE_ENV === 'production',
      },
    },
    csrfToken: {
      name: 'job-portal-next-auth.csrf-token',
      options: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: process.env.NODE_ENV === 'production',
      },
    },
    state: {
      name: 'job-portal-next-auth.state',
      options: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: process.env.NODE_ENV === 'production',
      },
    },
    nonce: {
      name: 'job-portal-next-auth.nonce',
      options: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: process.env.NODE_ENV === 'production',
      },
    },
    pkceCodeVerifier: {
      name: 'job-portal-next-auth.pkce.code_verifier',
      options: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: process.env.NODE_ENV === 'production',
      },
    },
  },

  // ** Please refer to https://next-auth.js.org/configuration/options#pages for more `pages` options
  pages: {
    signIn: '/login'
  },

  // ** Please refer to https://next-auth.js.org/configuration/options#callbacks for more `callbacks` options
  callbacks: {
    /*
     * While using `jwt` as a strategy, `jwt()` callback will be called before
     * the `session()` callback. So we have to add custom parameters in `token`
     * via `jwt()` callback to make them accessible in the `session()` callback
     */
    async jwt({ token, user }) {
      if (user) {
        /*
         * For adding custom parameters to user in session, we first need to add those parameters
         * in token which then will be available in the `session()` callback
         */
        const u = user as CredentialsUser
        token.id = user.id
        token.name = user.name
        token.email = user.email
        token.username = u.username
        token.role_id = u.role_id
        token.report_to = u.report_to
        token.tenants = u.tenants
        token.permissions = u.permissions ?? {}
        // Store the backend access token in the JWT token
        // This will be used to set it in localStorage on the client side
        if (u.access_token) {
          token.backend_access_token = u.access_token
        }
      }

      return token
    },
    async session({ session, token }) {
      if (session.user) {
        // ** Add custom params to user in session which are added in `jwt()` callback via `token` parameter
        session.user.id = token.id ?? ''
        session.user.name = token.name ?? ''
        session.user.email = token.email ?? ''
        // OAuth (e.g. Google) picture on token; keep for Avatar in shell UI
        if (token.picture) {
          session.user.image = token.picture
        }
        session.user.username = token.username
        session.user.role_id = token.role_id
        session.user.report_to = token.report_to
        session.user.tenants = token.tenants
        session.user.permissions = token.permissions ?? {}
        // Add backend access token to session for client-side storage
        if (token.backend_access_token) {
          session.backend_access_token = token.backend_access_token
        }
      }

      return session
    }
  }
}

// -----------------------------------------------------------------------------
// Helper for registration (used by Register view)
// -----------------------------------------------------------------------------

export interface RegisterPayload {
  email: string
  password: string
  en_name: string
  username?: string
}

/**
 * Call the backend `/auth/register` endpoint.
 * Throws an Error with message on failure.
 */
export async function registerUser(payload: RegisterPayload) {
  const API_URL = getRuntimeConfig('NEXT_PUBLIC_API_URL', 'http://localhost:5000')

  let res: Response
  try {
    res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    })
  } catch (fetchError: any) {
    console.error('[REGISTER ERROR] Network failure:', fetchError)
    throw new Error(`Failed to connect to server at ${API_URL}`)
  }

  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const errorMsg =
      typeof data.message === 'string'
        ? data.message
        : data.error || 'Registration failed. Please try again.'
    throw new Error(errorMsg)
  }

  return data
}
